/**
 * listingParsers.js — libellés et icônes des champs structurés d'une annonce (contraintes, préférences,
 * conditions, transports, catégories). L'ancien format, des détails encodés dans un texte, n'existe plus : ses
 * décodeurs ont été retirés le 2026-10-20 (N4) ; une annonce ne se lit que par ses champs.
 */

// ─── Métadonnées des contraintes colis (v2 — 15 items) ───────────────────────
export const CONSTRAINT_META = {
  fragile:      { icon: '🏺', label: 'Fragile' },
  very_fragile: { icon: '🔮', label: 'Très fragile' },
  upright:      { icon: '⬆️', label: 'À garder vertical' },
  no_stack:     { icon: '🚫', label: 'Ne pas empiler' },
  temp:         { icon: '🌡️', label: 'Température contrôlée' },
  cold_chain:   { icon: '❄️', label: 'Chaîne du froid' },
  perishable:   { icon: '🥗', label: 'Périssable' },
  electronic:   { icon: '💻', label: 'Électronique' },
  valuable:     { icon: '💎', label: 'Produit de valeur' },
  urgent:       { icon: '🚀', label: 'Urgent' },
  signature:    { icon: '✍️', label: 'Signature requise' },
  insurance:    { icon: '🛡️', label: 'Assurance souhaitée' },
  bulky:        { icon: '📦', label: 'Volumineux' },
  careful:      { icon: '🤲', label: 'Manipulation avec précaution' },
  other:        { icon: '📋', label: 'Autre contrainte' },
};

// ─── Métadonnées des préférences transporteur (v2 — 15 items) ────────────────
export const CARRIER_PREF_META = {
  verified:     { icon: '✅', label: 'Profil vérifié exigé' },
  professional: { icon: '🏢', label: 'Transporteur PRO' },
  particular:   { icon: '👤', label: 'Particulier accepté' },
  fast:         { icon: '⚡', label: 'Livraison rapide' },
  direct:       { icon: '🎯', label: 'Trajet direct' },
  no_stop:      { icon: '🚫', label: 'Sans escale' },
  weekend:      { icon: '📅', label: 'Disponible le week-end' },
  evening:      { icon: '🌙', label: 'Disponible le soir' },
  ac_vehicle:   { icon: '❄️', label: 'Véhicule climatisé' },
  insured:      { icon: '🛡️', label: 'Couverture assurance' },
  fragile_ok:   { icon: '🏺', label: 'Accepte fragiles' },
  bulky_ok:     { icon: '📦', label: 'Accepte volumineux' },
  french:       { icon: '🇫🇷', label: 'Parle français' },
  english:      { icon: '🇬🇧', label: 'Speaks English' },
  experienced:  { icon: '🏅', label: 'Transporteur expérimenté' },
};

// ─── Labels des conditions transporteur (liste complète) ─────────────────────
export const CONDITION_LABELS = {
  frag:         '🏺 Fragiles acceptés',
  temp:         '❄️ Température contrôlée',
  scelle:       '🔒 Colis scellés',
  ass:          '🛡️ Assurance disponible',
  otp:          '📱 Code OTP requis',
  photo:        '📸 Preuve photo',
  id_check:     '🪪 Vérif. identité expéditeur',
  pickup:       '🏠 Remise à domicile possible',
  airport_only: '✈️ Dépôt aéroport uniquement',
  contactless:  '🤝 Sans contact possible',
  trace:        '📍 Suivi en temps réel',
  sign:         '✍️ Signature à la livraison',
  double_check: '🔍 Double vérif. contenu',
  doc_required: "📄 Documents d'envoi requis",
};

// ─── Labels des moyens de transport ──────────────────────────────────────────
export const TRANSPORT_LABELS = {
  avion:   '✈️ Avion',
  voiture: '🚗 Voiture',
  bus:     '🚌 Bus / Car',
  bateau:  '⛴️ Bateau',
  train:   '🚆 Train',
  autre:   '📦 Autre',
};

// ─── Symboles monétaires — délégués à currencyUtils ──────────────────────────
export { CCY_SYM, ccySym } from '@/utils/currencyUtils';

// ─── Souplesse de la date et urgence (colis et trajet, 2026-11-03 — backend/listing_options.py) ──────────
// « souple » et « libre » : anciennes valeurs des trajets publiés avant le 2026-11-03, encore lisibles
export const FLEX_LABELS = {
  fixe: 'Date fixe', pm3j: '± 3 jours', pm1s: '± 1 semaine', pm2s: '± 2 semaines', souple: '± 3 jours', libre: 'Flexible',
};
export const URGENCY_LABELS = { standard: 'Pas pressé', priority: 'Prioritaire', urgent: 'Urgent' };

/** Libellé sans l'emoji de tête (« ✈️ Avion » → « Avion ») : les écrans refaits n'affichent que des icônes Lucide. */
export const plainLabel = (label) => String(label || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
